import { useState, useMemo, useRef, useEffect } from "react";
import "./DataTableListing.css";
import nodata from "../../assets/images/nodata.svg";

// ─── Icons ────────────────────────────────────────────────────────────────────
const SortIcon = ({ direction }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 2L10 6H4L7 2Z"
      fill={direction === "asc" ? "#6366f1" : "#cbd5e1"}
    />
    <path
      d="M7 12L4 8H10L7 12Z"
      fill={direction === "desc" ? "#6366f1" : "#cbd5e1"}
    />
  </svg>
);

const FilterIcon = ({ active }) => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M1.5 2.5h10l-4 5v3l-2-1V7.5l-4-5z"
      stroke={active ? "#6366f1" : "#94a3b8"}
      strokeWidth="1.2"
      fill={active ? "#6366f199" : "none"}
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M9 11L5 7l4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M5 3l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2 2l8 8M10 2l-8 8"
      stroke="#94a3b8"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// ─── Filter Dropdown ──────────────────────────────────────────────────────────
function FilterDropdown({ column, data, activeFilter, onFilter, onClose }) {
  const ref = useRef();
  const uniqueValues = useMemo(() => {
    const vals = [...new Set(data.map((row) => row[column.key]))].filter(
      (v) => v != null,
    );
    return vals.sort((a, b) =>
      typeof a === "number" ? a - b : String(a).localeCompare(String(b)),
    );
  }, [data, column.key]);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(activeFilter || []);

  const filtered = uniqueValues.filter((v) =>
    String(v)
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const toggle = (val) =>
    setSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );

  return (
    <div ref={ref} style={styles.filterDropdown}>
      <div style={styles.filterHeader}>
        <span style={styles.filterTitle}>Filter · {column.title}</span>
        <button onClick={onClose} style={styles.iconBtn}>
          <CloseIcon />
        </button>
      </div>
      <div style={{ padding: "8px 10px 4px" }}>
        <input
          style={styles.filterSearch}
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div style={styles.filterList}>
        {filtered.map((val) => (
          <label key={String(val)} style={styles.filterItem}>
            <input
              type="checkbox"
              checked={selected.includes(val)}
              onChange={() => toggle(val)}
              style={{ accentColor: "#6366f1" }}
            />
            <span style={styles.filterItemLabel}>{String(val)}</span>
          </label>
        ))}
        {filtered.length === 0 && (
          <div style={styles.noValues}>No values found</div>
        )}
      </div>
      <div style={styles.filterActions}>
        <button style={styles.btnSecondary} onClick={() => setSelected([])}>
          Reset
        </button>
        <button
          style={styles.btnPrimary}
          onClick={() => {
            onFilter(selected.length ? selected : null);
            onClose();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export const EditIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.25 12.9167H2.30125L10.8317 4.38625L9.78042 3.335L1.25 11.8654V12.9167ZM0 14.1667V11.3463L10.9921 0.358958C11.1181 0.244513 11.2572 0.156111 11.4094 0.09375C11.5617 0.03125 11.7215 0 11.8885 0C12.0556 0 12.2174 0.0296532 12.374 0.0889587C12.5306 0.148264 12.6693 0.242569 12.79 0.371875L13.8077 1.40229C13.937 1.52299 14.0292 1.66188 14.0842 1.81896C14.1392 1.97604 14.1667 2.13312 14.1667 2.29021C14.1667 2.45785 14.1381 2.61778 14.0808 2.77C14.0236 2.92236 13.9326 3.06153 13.8077 3.1875L2.82042 14.1667H0ZM10.2969 3.86979L9.78042 3.335L10.8317 4.38625L10.2969 3.86979Z"
      fill="#4D4D4D"
    />
  </svg>
);

export const StarIcon = ({ filled }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.78584 10.2965L6.41084 8.71312L9.03584 10.3173L8.34834 7.31729L10.6608 5.31729L7.61917 5.04646L6.41084 2.21312L5.2025 5.02562L2.16084 5.29646L4.47334 7.31729L3.78584 10.2965ZM6.41084 10.1827L3.16896 12.1377C3.04813 12.2083 2.92708 12.2379 2.80584 12.2267C2.68458 12.2154 2.57486 12.1746 2.47667 12.1042C2.37834 12.0336 2.30243 11.9415 2.24896 11.8277C2.19563 11.714 2.18708 11.5881 2.22334 11.4502L3.08396 7.76917L0.221877 5.29167C0.114932 5.19875 0.0468071 5.09111 0.0175016 4.96875C-0.0119429 4.84639 -0.00423455 4.7275 0.0406266 4.61208C0.0854877 4.49681 0.15014 4.40257 0.234585 4.32937C0.319029 4.25618 0.434446 4.20833 0.580835 4.18583L4.35792 3.85583L5.82438 0.379791C5.87771 0.250486 5.95861 0.15486 6.06709 0.0929159C6.17556 0.0309714 6.29014 0 6.41084 0C6.53153 0 6.64611 0.0309714 6.75459 0.0929159C6.86306 0.15486 6.94396 0.250486 6.99729 0.379791L8.46375 3.85583L12.2408 4.18583C12.3872 4.20833 12.5026 4.25618 12.5871 4.32937C12.6715 4.40257 12.7362 4.49681 12.781 4.61208C12.8259 4.7275 12.8336 4.84639 12.8042 4.96875C12.7749 5.09111 12.7067 5.19875 12.5998 5.29167L9.73771 7.76917L10.5983 11.4502C10.6346 11.5881 10.626 11.714 10.5727 11.8277C10.5192 11.9415 10.4433 12.0336 10.345 12.1042C10.2468 12.1746 10.1371 12.2154 10.0158 12.2267C9.89459 12.2379 9.77354 12.2083 9.65271 12.1377L6.41084 10.1827Z"
      fill="#4D4D4D"
    />
  </svg>
);

export const MoveIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.04479 16.0896L4.79167 12.8365L5.68271 11.9454L7.41979 13.6827V8.66979H2.41979L4.15688 10.3942L3.25313 11.2979L0 8.04479L3.24021 4.80438L4.13125 5.69542L2.40688 7.41979H7.41979V2.40688L5.68271 4.14417L4.79167 3.25313L8.04479 0L11.2979 3.25313L10.4069 4.14417L8.66979 2.40688V7.41979H13.6698L11.9327 5.69542L12.8365 4.79167L16.0896 8.04479L12.8365 11.2979L11.9454 10.4069L13.6827 8.66979H8.66979V13.6698L10.3942 11.9327L11.2979 12.8365L8.04479 16.0896Z"
      fill="#4D4D4D"
    />
  </svg>
);

export const DeleteIcon = () => (
  <svg
    width="13"
    height="15"
    viewBox="0 0 13 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.33979 14.0704C1.92549 14.0704 1.57083 13.9229 1.27583 13.6279C0.980833 13.3329 0.833333 12.9783 0.833333 12.564V1.98708H0.625C0.447917 1.98708 0.299514 1.92715 0.179792 1.80729C0.0599307 1.68743 0 1.53896 0 1.36188C0 1.18465 0.0599307 1.03625 0.179792 0.916667C0.299514 0.796944 0.447917 0.737083 0.625 0.737083H3.75C3.75 0.533056 3.82188 0.359167 3.96563 0.215417C4.10924 0.0718061 4.28306 0 4.48708 0H8.01292C8.21694 0 8.39076 0.0718061 8.53438 0.215417C8.67813 0.359167 8.75 0.533056 8.75 0.737083H11.875C12.0521 0.737083 12.2005 0.797014 12.3202 0.916875C12.4401 1.03674 12.5 1.18521 12.5 1.36229C12.5 1.53951 12.4401 1.68792 12.3202 1.8075C12.2005 1.92722 12.0521 1.98708 11.875 1.98708H11.6667V12.564C11.6667 12.9783 11.5192 13.3329 11.2242 13.6279C10.9292 13.9229 10.5745 14.0704 10.1602 14.0704H2.33979ZM10.4167 1.98708H2.08333V12.564C2.08333 12.6388 2.10736 12.7003 2.15542 12.7483C2.20347 12.7964 2.26493 12.8204 2.33979 12.8204H10.1602C10.2351 12.8204 10.2965 12.7964 10.3446 12.7483C10.3926 12.7003 10.4167 12.6388 10.4167 12.564V1.98708ZM4.71188 11.1538C4.88896 11.1538 5.03736 11.0939 5.15708 10.9742C5.27667 10.8543 5.33646 10.7058 5.33646 10.5288V4.27875C5.33646 4.10167 5.27653 3.95319 5.15667 3.83333C5.03694 3.71361 4.88847 3.65375 4.71125 3.65375C4.53417 3.65375 4.38576 3.71361 4.26604 3.83333C4.14646 3.95319 4.08667 4.10167 4.08667 4.27875V10.5288C4.08667 10.7058 4.14653 10.8543 4.26625 10.9742C4.38611 11.0939 4.53465 11.1538 4.71188 11.1538ZM7.78875 11.1538C7.96583 11.1538 8.11424 11.0939 8.23396 10.9742C8.35354 10.8543 8.41333 10.7058 8.41333 10.5288V4.27875C8.41333 4.10167 8.35347 3.95319 8.23375 3.83333C8.11389 3.71361 7.96535 3.65375 7.78813 3.65375C7.61104 3.65375 7.46264 3.71361 7.34292 3.83333C7.22333 3.95319 7.16354 4.10167 7.16354 4.27875V10.5288C7.16354 10.7058 7.22347 10.8543 7.34333 10.9742C7.46305 11.0939 7.61153 11.1538 7.78875 11.1538Z"
      fill="#4D4D4D"
    />
  </svg>
);
export const DownloadIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 13 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.25 9.09292C6.14958 9.09292 6.05611 9.07687 5.96958 9.04479C5.88306 9.01285 5.80076 8.95785 5.72271 8.87979L3.13146 6.28854C3.00757 6.16451 2.94639 6.01944 2.94792 5.85333C2.94958 5.68722 3.01076 5.53951 3.13146 5.41021C3.26076 5.28104 3.40924 5.21431 3.57687 5.21C3.74465 5.20569 3.89319 5.26819 4.0225 5.3975L5.625 7V0.625C5.625 0.447639 5.68486 0.299167 5.80458 0.179583C5.92417 0.059861 6.07264 0 6.25 0C6.42736 0 6.57583 0.059861 6.69542 0.179583C6.81514 0.299167 6.875 0.447639 6.875 0.625V7L8.4775 5.3975C8.60153 5.27361 8.74868 5.21243 8.91896 5.21396C9.08937 5.21563 9.23924 5.28104 9.36854 5.41021C9.48924 5.53951 9.55174 5.6859 9.55604 5.84937C9.56035 6.01285 9.49785 6.15924 9.36854 6.28854L6.77729 8.87979C6.69924 8.95785 6.61694 9.01285 6.53042 9.04479C6.44389 9.07687 6.35042 9.09292 6.25 9.09292ZM1.50646 12.5C1.08549 12.5 0.729167 12.3542 0.4375 12.0625C0.145833 11.7708 0 11.4145 0 10.9935V9.35896C0 9.1816 0.059861 9.03312 0.179583 8.91354C0.299167 8.79382 0.447639 8.73396 0.625 8.73396C0.802361 8.73396 0.950833 8.79382 1.07042 8.91354C1.19014 9.03312 1.25 9.1816 1.25 9.35896V10.9935C1.25 11.0577 1.27674 11.1165 1.33021 11.1698C1.38354 11.2233 1.44229 11.25 1.50646 11.25H10.9935C11.0577 11.25 11.1165 11.2233 11.1698 11.1698C11.2233 11.1165 11.25 11.0577 11.25 10.9935V9.35896C11.25 9.1816 11.3099 9.03312 11.4296 8.91354C11.5492 8.79382 11.6976 8.73396 11.875 8.73396C12.0524 8.73396 12.2008 8.79382 12.3204 8.91354C12.4401 9.03312 12.5 9.1816 12.5 9.35896V10.9935C12.5 11.4145 12.3542 11.7708 12.0625 12.0625C11.7708 12.3542 11.4145 12.5 10.9935 12.5H1.50646Z"
      fill="#4D4D4D"
    />
  </svg>
);

// ─── DataTable ────────────────────────────────────────────────────────────────
/**
 * DataTable
 *
 * @param {Object[]} columns        Column definitions
 * @param {string}   columns[].key         Data key
 * @param {string}   columns[].title       Header label
 * @param {boolean}  [columns[].sortable]  Enable sorting
 * @param {boolean}  [columns[].filterable] Enable filtering
 * @param {Function} [columns[].render]    Custom cell renderer (value, row, idx, isHovered) => node
 * @param {number|string} [columns[].width] Column width
 * @param {string}   [columns[].align]     "left" | "center" | "right"
 *
 * @param {Object[]} dataSource        Row data
 * @param {string}   [rowKey="key"]    Unique row identifier field
 * @param {Object|false} [pagination]  { pageSize: number } or false to disable
 * @param {boolean}  [checkable]       Show checkbox column
 * @param {Function} [onSelectChange]  (selectedKeys, selectedRows) => void
 * @param {boolean}  [loading]         Show loading state
 * @param {string}   [emptyText]       Text when no data
 * @param {boolean}  [bordered]        Show cell borders
 * @param {"default"|"small"|"large"} [size] Row density
 * @param {Function} [onRow]           Row click handler (row, idx) => void
 */
export default function DataTableListing({
  columns = [],
  dataSource = [],
  rowKey = "key",
  pagination = false, //{ pageSize: 10 },
  checkable = false,
  onSelectChange,
  loading = false,
  emptyText = "No data",
  LineTwoText="",
  bordered = false,
  size = "default",
  onRow,
  rowHoverColor = "#F7F7F7",
  rowActions,
  scrollWrapperStyle = {},
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  // const [currentPage, setCurrentPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [hoveredKey, setHoveredKey] = useState(null);

  // const pageSize = pagination ? pagination.pageSize ?? 10 : Infinity;
  const hasRowActions = Array.isArray(rowActions) && rowActions.length > 0;
const safeDataSource = Array.isArray(dataSource) ? dataSource : [];

  // ── Filter
  const filteredData = useMemo(() => {
    let data = [...safeDataSource];
    Object.entries(filters).forEach(([key, vals]) => {
      if (vals?.length) data = data.filter((row) => vals.includes(row[key]));
    });
    return data;
  }, [safeDataSource, filters]);

  // ── Sort
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const av = a[sortConfig.key],
        bv = b[sortConfig.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortConfig]);

  const isServerPagination =
    pagination && typeof pagination === "object" && !!pagination.onChange;

  // 2. Replace the single useState for currentPage with this:
  const [internalPage, setInternalPage] = useState(1);
  const currentPage = isServerPagination
    ? pagination.currentPage
    : internalPage;
  // ── Pagination
  const totalPages = isServerPagination
    ? Math.max(
        1,
        Math.ceil((pagination.total ?? 0) / (pagination.pageSize ?? 10)),
      )
    : pagination
    ? Math.max(1, Math.ceil(sortedData.length / (pagination.pageSize ?? 10)))
    : 1;
const isSinglePage = totalPages === 1;
  // 4. Replace pagedData calculation:
  const pagedData = isServerPagination
    ? sortedData // backend already sliced — show all rows as-is
    : pagination
    ? sortedData.slice(
        (currentPage - 1) * (pagination.pageSize ?? 10),
        currentPage * (pagination.pageSize ?? 10),
      )
    : sortedData;

  // 5. Replace the page-reset useEffect:
  useEffect(() => {
    if (!isServerPagination) setInternalPage(1);
  }, [filters, sortConfig]);

  // 6. Add a unified page change handler:
  const handlePageChange = (page) => {
    if (isServerPagination) {
      pagination.onChange(page);
    } else {
      setInternalPage(page);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: null };
    });
  };

  // ── Checkbox
  const getRowKey = (row) => row[rowKey] ?? row.id ?? row.key;
  const pageKeys = pagedData.map(getRowKey);
  const allPageSelected =
    pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.includes(k));
  const somePageSelected =
    pageKeys.some((k) => selectedKeys.includes(k)) && !allPageSelected;

  const toggleAll = () => {
    const next = allPageSelected
      ? selectedKeys.filter((k) => !pageKeys.includes(k))
      : [...new Set([...selectedKeys, ...pageKeys])];
    setSelectedKeys(next);
    onSelectChange?.(
      next,
      safeDataSource.filter((r) => next.includes(getRowKey(r))),
    );
  };

  const toggleRow = (key) => {
    const next = selectedKeys.includes(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    setSelectedKeys(next);
    onSelectChange?.(
      next,
      safeDataSource.filter((r) => next.includes(getRowKey(r))),
    );
  };

  // ── Size config
  const cellPad =
    size === "small"
      ? "7px 12px"
      : size === "large"
      ? "16px 20px"
      : "11px 16px";
  const fontSize =
    size === "small" ? "12.5px" : size === "large" ? "15px" : "13.5px";

  // ── Pagination helper
  const pageNumbers = useMemo(() => {
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    )
      range.push(i);

    const pages = [1];
    if (range[0] > 2) pages.push("...");
    pages.push(...range);
    if (range[range.length - 1] < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div 
    // style={{ ...styles.wrapper, ...(bordered ? styles.borderedWrapper : {}) }}
    >
     <div style={{ overflowX: openFilter ? "visible" : "auto", ...scrollWrapperStyle }}>
        <table
        // style={{ ...styles.table, ...(bordered ? styles.borderedTable : {}) }}
        >
          <thead className="border-b border-[#D9D9D9]"  style={{
            position: "sticky",
            top: "-1px",
            zIndex: 0,
            background: "#fff",
            boxShadow:" 0px 0px 12px 0px #0000001F"
          }}>
            <tr>
              {checkable && (
                <th
                // style={{ ...styles.th, padding: cellPad, width: 44, textAlign: "center" }}
                >
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected;
                    }}
                    onChange={toggleAll}
                    style={{
                      accentColor: "#6366f1",
                      width: 15,
                      height: 15,
                      cursor: "pointer",
                    }}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  // style={{
                  //   ...styles.th,
                  //   padding: cellPad,
                  //   fontSize,
                  //   width: col.width || "auto",
                  //   textAlign: col.align || "left",
                  // }}
                  className="head-cell-dataTable"
                  style={col.headerStyle}
                >
                  <div
                    style={{ margin: "16px 0" }}
                    // style={{ ...styles.thInner, justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start" }}
                  >
                    <span
                      style={{
                        cursor: col.sortable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      {col.title}
                    </span>
                    <div
                    // style={styles.thActions}
                    >
                      {col.sortable && (
                        <button
                          style={styles.iconBtn}
                          title={`Sort by ${col.title}`}
                          onClick={() => handleSort(col.key)}
                        >
                          <SortIcon
                            direction={
                              sortConfig.key === col.key
                                ? sortConfig.direction
                                : null
                            }
                          />
                        </button>
                      )}
                      {col.filterable && (
                        <div style={{ position: "relative" }}>
                          <button
                            style={{
                              ...styles.iconBtn,
                              background: filters[col.key]?.length
                                ? "#eef2ff"
                                : "transparent",
                              borderRadius: 4,
                            }}
                            title={`Filter ${col.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFilter(
                                openFilter === col.key ? null : col.key,
                              );
                            }}
                          >
                            <FilterIcon active={!!filters[col.key]?.length} />
                          </button>
                          {openFilter === col.key && (
                            <FilterDropdown
                              column={col}
                              data={safeDataSource}
                              activeFilter={filters[col.key]}
                              onFilter={(vals) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [col.key]: vals,
                                }))
                              }
                              onClose={() => setOpenFilter(null)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (checkable ? 1 : 0)}
                  style={{
                    ...styles.td,
                    textAlign: "center",
                    padding: "52px 16px",
                  }}
                >
                  <div style={styles.spinner} />
                  <div
                    style={{ marginTop: 12, color: "#94a3b8", fontSize: 13 }}
                  >
                    Loading…
                  </div>
                </td>
              </tr>
            ) : pagedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (checkable ? 1 : 0)}
                  style={{
                    ...styles.td,
                    textAlign: "center",
                    padding: "52px 16px",
                    color: "#94a3b8",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 8 }}>
                    <img src={nodata} alt="no data" />
                  </div>
                  {emptyText}
                  {LineTwoText && <div style={{ marginTop: 4 }}>{LineTwoText}</div>}
                </td>
              </tr>
            ) : (
              pagedData.map((row, idx) => {
                const key = getRowKey(row);
                const isSelected = selectedKeys.includes(key);
                const isHovered = hoveredKey === key;
                const rowBg = isSelected
                  ? "#eef2ff"
                  : isHovered
                  ? rowHoverColor
                  : "#ffffff";
                return (
                  <tr
                    key={key}
                    style={{
                      background: rowBg,
                      cursor: onRow ? "pointer" : "default",
                    }}
                    onMouseEnter={() => setHoveredKey(key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    // style={{
                    //   // ...styles.tr,
                    //   background: isSelected
                    //     ? "#f0f4ff"
                    //     : idx % 2 === 0
                    //       ? "#ffffff"
                    //       : "#f8fafc",
                    //   cursor: onRow ? "pointer" : "default",
                    // }}
                    onClick={() => onRow?.(row, idx)}
                  >
                    {checkable && (
                      <td
                        style={{
                          ...styles.td,
                          padding: cellPad,
                          textAlign: "center",
                          width: 44,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(key);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(key)}
                          style={{
                            accentColor: "#6366f1",
                            width: 15,
                            height: 15,
                            cursor: "pointer",
                          }}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        // style={{
                        //   ...styles.td,
                        //   padding: cellPad,
                        //   fontSize,
                        //   textAlign: col.align || "left",
                        //   ...(bordered ? { borderRight: "1px solid #f1f5f9" } : {}),
                        // }}
                        className="td-cell-dataTable truncate-cell"
                        title={String(row[col.key] ?? "")}
                      >
                        {col.render
                          ? col.render(row[col.key], row, idx, isHovered)
                          : row[col.key] ?? "—"}
                      </td>
                    ))}
                    {hasRowActions && (
                      <td
                        style={{
                          position: "relative",
                          overflow: "visible",
                          width: rowActions.length * 36 + 28,
                          minWidth: rowActions.length * 36 + 28,
                          // width:'100%',
                          padding: "0 8px",
                        }}
                      >
                        <RowActionsTray
                          actions={rowActions}
                          row={row}
                          visible={isHovered}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {pagination !== false && (
        <div style={styles.footer}>
          {/* <div style={styles.footerLeft}>
            {selectedKeys.length > 0 && (
              <span style={styles.selectedBadge}>{selectedKeys.length} selected</span>
            )} */}
          {/* <span style={{ color: "#94a3b8", fontSize: 12.5 }}>
              {sortedData.length} result{sortedData.length !== 1 ? "s" : ""}
            </span> */}
          {/* </div> */}
          <div></div>

          <div style={styles.pagination}>
            {/* <button
              style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              title="First page"
            >«</button> */}
            <button
              style={{
                ...styles.pageBtn,
                ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
              }}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              title="Previous page"
            >
              <ChevronLeft />
            </button>

            {pageNumbers.map((item, i) =>
              item === "..." ? (
                <span key={`e${i}`} style={styles.ellipsis}>
                  …
                </span>
              ) : (
                <button
                  key={item}
                  style={{
                    ...styles.pageBtn,
                    ...(item === currentPage ? styles.pageBtnActive : {}),
                     cursor: item === currentPage ? "default" : "pointer", 
                  }}
                  onClick={() => item !== currentPage && handlePageChange(item)} 
                >
                  {item}
                </button>
              ),
            )}

            <button
              style={{
                ...styles.pageBtn,
                ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
              }}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              title="Next page"
            >
              <ChevronRight />
            </button>
            {/* <button
              style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Last page"
            >»</button> */}
          </div>
          {/* <span style={{ color: "#cbd5e1", margin: "0 4px", fontSize: 13 }}>|</span> */}
          <span
            style={{ fontSize: 12.5, color: "#333333", whiteSpace: "nowrap" }}
          >
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
}

export function RowActionButton({ icon, label, color, hoverBg, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          border: `1px solid ${hovered ? color + "55" : "transparent"}`,
          background: hovered ? hoverBg : "transparent",
          color: hovered ? color : "#94a3b8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.15s ease",
          flexShrink: 0,
        }}
      >
        {icon}
      </button>
      {hovered && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 5px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1e293b",
            color: "#fff",
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: "nowrap",
            padding: "3px 8px",
            borderRadius: 5,
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,.2)",
          }}
        >
          {label}
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "4px solid #1e293b",
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
     ROW ACTIONS TRAY
  ══════════════════════════════════════════════════════════════ */
export function RowActionsTray({ actions, row, visible }) {
  return (
    <div
      style={{
        position: "absolute",
        // right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        display: "flex",
        alignItems: "center",
        gap: 3,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.15s ease",
        pointerEvents: visible ? "auto" : "none",
        zIndex: 10,
      }}
    >
      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", marginRight: 3 }}
      />
      {actions.map((action) => (
        <RowActionButton
          key={action.key}
          icon={action.icon}
          label={action.label}
          color={action.color || "#6366f1"}
          hoverBg={action.hoverBg || "#eef2ff"}
          onClick={() => action.onClick?.(row)}
        />
      ))}
    </div>
  );
}

export const EditAction = {
  key: "edit",
  label: "Edit",
  icon: <EditIcon />,
  color: "#6366f1",
  hoverBg: "#eef2ff",
};
export const FavouriteAction = {
  key: "favourite",
  label: "Favourite",
  icon: <StarIcon />,
  color: "#f59e0b",
  hoverBg: "#fef9c3",
};
export const MoveAction = {
  key: "move",
  label: "Move",
  icon: <MoveIcon />,
  color: "#0ea5e9",
  hoverBg: "#e0f2fe",
};
export const DeleteAction = {
  key: "delete",
  label: "Delete",
  icon: <DeleteIcon />,
  color: "#ef4444",
  hoverBg: "#fee2e2",
};
export const DownloadAction = {
  key: "donwnload",
  label: "Download",
  icon: <DownloadIcon />,
  color: "#ef4444",
  hoverBg: "#fee2e2",
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  tr: {
    transition: "background 0.1s ease",
  },
  iconBtn: {
    background: "none",
    border: "none",
    padding: "3px",
    cursor: "pointer",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    transition: "background 0.15s",
  },
  // Filter dropdown
  filterDropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    zIndex: 9999,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06)",
    width: 220,
    overflow: "hidden",
  },
  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px 8px",
    borderBottom: "1px solid #f1f5f9",
    background: "#fafbfc",
  },
  filterTitle: {
    fontSize: 11.5,
    fontWeight: 600,
    color: "#6366f1",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  filterSearch: {
    width: "100%",
    padding: "7px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 13,
    outline: "none",
    color: "#0f172a",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  filterList: {
    maxHeight: 180,
    overflowY: "auto",
    padding: "4px 0",
  },
  filterItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    cursor: "pointer",
    fontSize: 13,
    color: "#374151",
    userSelect: "none",
    transition: "background 0.1s",
  },
  filterItemLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  noValues: {
    padding: "16px 14px",
    color: "#94a3b8",
    fontSize: 13,
    textAlign: "center",
  },
  filterActions: {
    display: "flex",
    gap: 8,
    padding: "10px 12px",
    borderTop: "1px solid #f1f5f9",
    justifyContent: "flex-end",
    background: "#fafbfc",
  },
  btnPrimary: {
    background: "#6366f1",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnSecondary: {
    background: "#f1f5f9",
    color: "#64748b",
    border: "none",
    borderRadius: 6,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // Footer
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    flexWrap: "wrap",
    gap: 8,
    width: "100%",
    marginTop: "10px",
  },
  footerLeft: { display: "flex", alignItems: "center", gap: 10 },
  selectedBadge: {
    background: "#eef2ff",
    color: "#6366f1",
    border: "1px solid #c7d2fe",
    borderRadius: 20,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 600,
  },
  pagination: { display: "flex", alignItems: "center", gap: 12 },
  pageBtn: {
    minWidth: 30,
    height: 30,
    border: "1px solid #F1F1F1",
    background: "#fff",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    color: "#666666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
    fontWeight: 500,
    transition: "all 0.12s",
    fontFamily: "inherit",
    outline: "none",
  },
  pageBtnActive: {
    background: "#0068FF",
    color: "#fff",
    borderColor: "#F1F1F1",
    fontWeight: 700,
    boxShadow: "0 2px 8px rgba(99,102,241,.3)",
  },
  pageBtnDisabled: { opacity: 0.3, cursor: "not-allowed" },
  ellipsis: {
    padding: "0 2px",
    color: "#94a3b8",
    fontSize: 14,
    lineHeight: "30px",
  },
  spinner: {
    width: 26,
    height: 26,
    border: "3px solid #e2e8f0",
    borderTopColor: "#6366f1",
    borderRadius: "50%",
    animation: "spin 0.75s linear infinite",
    margin: "0 auto",
  },
};
